<script setup lang="ts">
import { computed } from 'vue';
import { useRequestSolvedCustomisation } from '../composables/useRequestSolvedCustomisation';
import { useRecentResolvedTicketQuery } from '../composables/useRecentResolvedTicketQuery';
import { truncateText, stripHtml } from '../utils/textUtils';
import { useRouter } from 'vue-router/composables';
import * as SUtils from '@/common/customisation/utils/styleUtils';

const router = useRouter();
const customisation = useRequestSolvedCustomisation();

// Use TanStack Query for fetching ticket data
const { ticket } = useRecentResolvedTicketQuery();

const containerStyle = computed(() => SUtils.createBackgroundStyle(customisation.backgroundColor));
const labelStyle = computed(() => SUtils.createTextStyle(customisation.labelColor));
const messageStyle = computed(() => SUtils.createTextStyle(customisation.subTextColor));
const actionStyle = computed(() => SUtils.createTextStyle(customisation.actionColor));

const shouldRender = computed(() => {
  if (!customisation.isVisible.value) {
    return false;
  }
  // Only show if we have a ticket loaded
  return !!ticket.value && customisation.labelText.value?.trim() !== '';
});

// Display last comment message as subtext (truncated to 60 chars)
const ticketMessage = computed(() => {
  // Get the last comment
  const lastComment = ticket.value?.comments?.[ticket.value?.comments.length - 1];
  const commentText = lastComment?.body || lastComment?.message || ticket.value?.subject || '';

  if (!commentText) {
    return '';
  }

  // Strip HTML and truncate
  const plainText = stripHtml(commentText);
  return truncateText(plainText, 60);
});

const handleViewClick = () => {
  if (!ticket.value) {
    return;
  }

  router.push({ path: `/conversation/${ticket.value.id}`, query: { status: 'RESOLVED' } });
};
</script>

<template>
  <div v-if="shouldRender" class="font-theme-main request-solved-row" :style="containerStyle">
    <div class="content">
      <div class="label-wrapper">
        <span class="label theme-font-main" :style="labelStyle">
          {{ customisation.labelText.value }}
        </span>
      </div>
      <p v-if="ticketMessage" class="message theme-font-subtext" :style="messageStyle">
        {{ ticketMessage }}
      </p>
    </div>
    <div class="action-wrapper">
      <button class="action-button theme-font-subtext" :style="actionStyle" @click="handleViewClick">
        {{ customisation.actionText.value }}
      </button>
      <v-icon name="FaChevronRight" class="chevron-icon" :style="actionStyle" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.request-solved-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  gap: 16px;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;

  .label {
    font-weight: 500;
  }
}

.message {
  margin: 0;
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.chevron-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}
</style>
